import { useNavigate } from "@tanstack/react-router";

import { useAppDispatch } from "@/store/hooks";
import { authSliceActions } from "@/store/slices";

import { authApi } from "./auth.api";
import { LoginDriver, LoginExped, SaveRegisterDriver } from "./auth.types";

type AuthType = "expedition" | "driver";

type LoginHandlerBody<T extends AuthType> = T extends "expedition" ? LoginExped["args"] : T extends "driver" ? LoginDriver["args"] : never;

function useAuth() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loginDriverTrigger, loginTriggerState] = useLoginDriverMutation();
  const [loginExpedTrigger, loginExpedTriggerState] = useLoginExpedMutation();
  const [saveRegisterDriverTrigger, saveRegisterDriverState] = useSaveRegisterDriverMutation();

  const LoginHandler =
    <TType extends AuthType>(type: TType) =>
    async (body: LoginHandlerBody<TType>) => {
      type LoginResponse = LoginDriver["result"] | LoginExped["result"];

      const matcher = new Map<AuthType, () => Promise<LoginResponse>>([
        [
          "driver",
          async () => {
            return await loginDriverTrigger(body as LoginDriver["args"]).unwrap();
          }
        ],
        [
          "expedition",
          async () => {
            return await loginExpedTrigger(body as LoginExped["args"]).unwrap();
          }
        ]
      ]);
      const keys = Array.from(matcher.keys());
      const handler = matcher.get(type);

      if (!handler) throw new Error(`The ${type} are not valid! Only are accepted ${keys.join(", ")}!`);

      return (await handler()) as TType extends "expedition" ? LoginExped["result"] : TType extends "driver" ? LoginDriver["result"] : never;
    };

  const LogoutHandler = (type: AuthType) => {
    const matcher = new Map<AuthType, () => void>([
      ["driver", () => navigate({ to: "/app/login" })],
      ["expedition", () => navigate({ to: "/expedicao/login" })]
    ]);

    const keys = Array.from(matcher.keys());
    const handler = matcher.get(type);

    if (!handler) return console.error(`The ${type} are not valid! Only are accepted ${keys.join(", ")}!`);
    dispatch(authSliceActions.logout());
    handler();
  };

  const SignIn = LoginHandler;
  const SignOut = LogoutHandler;
  const SignUpDriver = async (body: SaveRegisterDriver["args"]) => {
    return await saveRegisterDriverTrigger(body);
  };

  return {
    state: {
      signInDriver: loginTriggerState,
      signInExped: loginExpedTriggerState,
      signUpDriver: saveRegisterDriverState
    },
    SignIn,
    SignOut,
    SignUpDriver
  };
}

export const { useLoginDriverMutation, useLoginExpedMutation, useSaveRegisterDriverMutation } = authApi;
export { useAuth };
